import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AppState } from '../../../angular-wallet-base/store/appState';
import { supportedAssetList } from '../../../angular-wallet-base/lib/supported-assets/supported-assets';
import { createKeyringId } from '../../../angular-wallet-base/keyringUtils';
import { EpinManagerService } from '../../../angular-wallet-base/services/epin-manager.service';
import { MembersPortalService } from '../../../angular-wallet-base/services/members-portal.service';
import { calculateLode, getToastPosition, gramsToOz, ozToGrams } from '../../../angular-wallet-base/utils';
import { AlertController, NavController, Platform, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BigNumber } from 'bignumber.js';
import { ChangeSecondatyTitleAction, ChangeTitleAction, UseI18nOnTitleAction } from 'src/app/angular-wallet-base/actions/appSettings.actions';
import { SubscriptionListenerComponent } from 'src/app/angular-wallet-base/SubscriptionListenerComponent';
import { getBaseCurrency } from 'src/app/angular-wallet-base/store/userPreferences';
import { take } from 'rxjs/operators';
import { AppModeService } from '../../../angular-wallet-base/services/app-mode.service';

interface CardOrder {
  grams: number
  oz: number
  metal_type: 'gold'|'silver'
  user_message: string
}

@Component({
  selector: 'app-silver-bullion',
  templateUrl: './silver-bullion.page.html',
  styleUrls: ['./silver-bullion.page.scss'],
})
export class SilverBullionPage extends SubscriptionListenerComponent implements OnInit {
  public tokenSymbol;
  public tokenGuid;
  public baseChainSymbol;
  public tokenInfo;
  public form: FormGroup;
  public cart = [];
  public lodeRateGrams;
  public publicInfo;
  public valueToFiat;
  public qtyTotal = 0;
  public ozTotal = 0;
  public lodeTotal = 0;
  public lodeTotalMin = 0;
  public lodeTotalMax = 0;
  public exchangeRate = 0;
  public itemArr = [];
  public loadingDelivery = false;
  public baseCurrency;
  public isDesktopMode;

  constructor(private activatedRoute: ActivatedRoute,
              private formBuilder: FormBuilder,
              private epin: EpinManagerService,
              protected store: Store<AppState>,
              private alertController: AlertController,
              private toast: ToastController,
              private translate: TranslateService,
              private appMode: AppModeService,
              public nav: NavController,
              private platform: Platform,
              private membersPortal: MembersPortalService) {
    super(store);
    this.appMode.isDesktopMode.subscribe(mode => this.isDesktopMode = mode);
  }

  async ngOnInit() {
    this.baseChainSymbol = this.activatedRoute.snapshot.queryParams.baseChainSymbol;
    this.tokenGuid = this.activatedRoute.snapshot.queryParams.guid;
    this.tokenSymbol = this.activatedRoute.snapshot.queryParams.symbol;
    this.tokenInfo = supportedAssetList[this.getActiveKeyringId()];
    this.initForm();
    this.getInfoForCalculation();
  }
  
  initForm() {
    this.form = this.formBuilder.group({
      type: ['silver', Validators.required],
      category: ['coin', Validators.required],
      qty: ['', [Validators.required, Validators.min(0)]],
      weight: ['', [Validators.required, this.CheckMinimumOz()]],
      amount: ['', Validators.required],
      amount_fiat: [0, Validators.required],
      notes: [''],
      promo: ['']
    });
  }
  
  async getInfoForCalculation() {
    this.baseCurrency = await this.store.select(getBaseCurrency).pipe(take(1)).toPromise();
    this.publicInfo = await this.membersPortal.getPublicInfo();
    this.lodeRateGrams = this.publicInfo.data.lode_rate;
    let quote;
    try {
      quote = await this.membersPortal.getPriceQuote(10, '', '1', 'lode', this.baseCurrency);
      this.exchangeRate = quote.data.ppt;
    } catch(err) {
      // wont show token rate
    }
  }

  addItemToCard() {
    
    if(this.form.invalid) {
      return;
    }

    const item = {
      id: Math.random().toString(16),
      type: this.form.get('type').value,
      category: this.form.get('category').value,
      qty: this.form.get('qty').value,
      weight: this.form.get('weight').value,
      amount: this.form.get('amount').value,
      notes: this.form.get('notes').value
    };
    
    this.cart.push(item);
    this.calculateCart();
    this.initForm();
  }
  
  calculateCart() {
    this.qtyTotal = 0;
    this.ozTotal = 0;
    this.lodeTotal = 0;
    this.itemArr = [];
    this.cart.forEach(item => {
      
      // Calculate Quantity total
      this.qtyTotal = this.qtyTotal + parseFloat(item.qty);
      // Calculate Oz total
      this.ozTotal = this.ozTotal + parseFloat(item.weight);
      // Calculate the Total Weight
      let totalWeight = (parseFloat(item.qty) * parseFloat(item.weight));
      // Building object to send
      var itemObj: CardOrder = {
        grams: ozToGrams(totalWeight),
        oz: totalWeight,
        metal_type: item.type,
        user_message: item.notes
      }
      this.itemArr.push(itemObj);
      // Calculate LODE total
      this.lodeTotal = this.lodeTotal + calculateLode(parseFloat(item.weight) , parseFloat(item.qty), item.type, this.lodeRateGrams);
       
    });
    
    // Adding 1% 
    this.lodeTotalMax = this.lodeTotal + (this.lodeTotal * 0.01);
    // Subst 1%
    this.lodeTotalMin = this.lodeTotal - (this.lodeTotal * 0.01);
  }

  setFormField(field, value) {
    this.form.get(field).setValue(value);
    if(field === 'type') { 
      this.calculateLode(value);
    }
  }
  
  getRoundNumber2decimals(num) {
    return Math.round(num * 100) / 100;
  }

  deleteCartItem(item) {
    this.cart = this.cart.filter(cartItem => item.id !== cartItem.id);
    this.calculateCart();
  }
  CheckMinimumOz = (): ValidatorFn => {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      if (isNaN(c.value) || parseInt(c.value) < 100) {
        return { 'minValue': true };
      }
      return null;
    };
  }
  async onSubmit() {
    try{
      this.loadingDelivery = true;
      var data: any = await this.membersPortal.createBullionOrder(this.itemArr);
      if (data.status === 0) {
        this.loadingDelivery = false;
        var msg = 'buy_lode.silver_bullion.messages.order_fail';
        var title = 'buy_lode.silver_bullion.messages.fail'
        this.presentToast(title, msg);
        return;
      }
      this.loadingDelivery = false;
      var msg = 'buy_lode.silver_bullion.messages.order_confirmation';
      var title = 'buy_lode.silver_bullion.messages.success';
      this.showAlertConfirmation(title, msg);
      // Reset form and cart
      this.initForm();
      this.cart = [];
      this.calculateCart();
    } catch (err) {
      this.loadingDelivery = false;
      var msg = 'buy_lode.silver_bullion.messages.min_oz_required';
      var title = 'buy_lode.silver_bullion.messages.warning'
      this.presentToast(title, msg);
    }
  }
  
  async showAlertConfirmation(title, msg) {
    const alert = await this.alertController.create({
      header: await this.translate.get(title).toPromise(),
      message: await this.translate.get(msg).toPromise(),
      buttons: ['OK']
    });
    return await alert.present();
  }

  getActiveKeyringId() {
    return createKeyringId(this.baseChainSymbol, this.tokenGuid);
  }

  capitalizeString(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  
  calculateLode(value = 'silver') {
    var weight = isNaN(parseFloat(this.form.get('weight').value)) ? 0 :  parseFloat(this.form.get('weight').value);
    var qty = isNaN(parseFloat(this.form.get('qty').value)) ? 0 : parseFloat(this.form.get('qty').value);
    var amount = calculateLode(weight , qty, value, this.lodeRateGrams);
    this.form.get('amount').setValue(amount);
    var fiat_amount = this.exchangeRate * amount;
    this.form.get('amount_fiat').setValue( this.getRoundNumber2decimals(fiat_amount));
  }
  
  onCancel() {
    this.nav.navigateBack(`/wallet/buy-tokens/step-one?guid=${this.tokenGuid}&symbol=${this.tokenSymbol}&baseChainSymbol=${this.baseChainSymbol}`);
  }
  
  isAddingDisabled() {
    return !this.form.valid;
  }
  
  async presentToast(hdr,msg) {
    const toast = await this.toast.create({
      header: await this.translate.get(hdr).toPromise(),
      message: await this.translate.get(msg).toPromise(),
      position: getToastPosition(this.platform),
      duration: 4000
    });
    await toast.present();
  }
  
  async ngAfterViewInit() {
    const i18nTitle = await this.translate.get('buy_lode.title_one', { tokenSymbol: this.tokenSymbol.toUpperCase() }).toPromise();
    const i18nSecondaryTitle = await this.translate.get('buy_lode.title_two').toPromise();
    this.store.dispatch(new ChangeTitleAction(i18nTitle));
    this.store.dispatch(new ChangeSecondatyTitleAction(i18nSecondaryTitle));
    this.store.dispatch(new UseI18nOnTitleAction(false));
  }
}
